<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const props = defineProps({
    birthdays: {
        type: Array,
        required: true,
    },
})

const activeIndex = ref(0)
const autoplayInterval = ref(null)

const activeBirthday = computed(() => props.birthdays[activeIndex.value] ?? props.birthdays[0])

const nextBirthday = () => {
    activeIndex.value = (activeIndex.value + 1) % props.birthdays.length
}

onMounted(() => {
    autoplayInterval.value = window.setInterval(nextBirthday, 5000)
})

onBeforeUnmount(() => {
    if (autoplayInterval.value) {
        window.clearInterval(autoplayInterval.value)
    }
})
</script>

<template>
    <section class="mural-birthday">
        <h2 class="mural-birthday__heading">{{ $t('components.rhdigital.mural.birthday.heading') }}</h2>

        <div class="mural-birthday__avatar" :key="activeBirthday.id">
            <img :src="activeBirthday.avatar" :alt="activeBirthday.name">
            <span class="mural-birthday__badge">
                <span class="material-symbols-outlined">bolt</span>
            </span>
        </div>

        <h3>{{ $t('components.rhdigital.mural.birthday.title', { name: activeBirthday.name }) }}</h3>
        <p>{{ $t('components.rhdigital.mural.birthday.subtitle', { count: birthdays.length }) }}</p>

        <div class="mural-birthday__actions">
            <button class="mural-birthday__congrats" type="button" v-tooltip="$t('common.labels.availableSoon')">
                <span class="material-symbols-outlined">send</span>
                {{ $t('components.rhdigital.mural.birthday.action') }}
            </button>
            <button
                class="mural-birthday__next"
                type="button"
                :aria-label="$t('components.rhdigital.mural.birthday.next')"
                @click="nextBirthday"
            >
                <span class="material-symbols-outlined">chevron_right</span>
            </button>
        </div>

        <div class="mural-birthday__dots" aria-hidden="true">
            <span
                v-for="(birthday, index) in birthdays"
                :key="birthday.id"
                class="mural-birthday__dot"
                :class="{ 'mural-birthday__dot--active': index === activeIndex }"
            ></span>
        </div>
    </section>
</template>

<style lang="scss" scoped>
.mural-birthday {
    padding: 26px 20px 20px;
    border: 1px solid #dde5f3;
    border-radius: 8px;
    background: #fff;
    text-align: center;
    box-shadow: 0 12px 30px rgba(34, 48, 73, 0.04);

    &__heading {
        margin: 0 0 18px;
        color: #2f3b52;
        font-size: 17px;
        font-weight: 850;
    }

    &__avatar {
        position: relative;
        display: inline-flex;
        width: 78px;
        height: 78px;
        padding: 5px;
        border-radius: 50%;
        background: #f4f6ff;

        img {
            width: 100%;
            height: 100%;
            border-radius: inherit;
            object-fit: cover;
            animation: birthday-fade 0.22s ease;
        }
    }

    &__badge {
        position: absolute;
        right: -2px;
        bottom: 6px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border: 3px solid #fff;
        border-radius: 50%;
        background: #625bf6;
        color: #fff;

        .material-symbols-outlined {
            font-size: 15px;
        }
    }

    h3 {
        margin: 22px 0 6px;
        color: #2f3b52;
        font-size: 17px;
        font-weight: 800;
    }

    p {
        margin: 0;
        color: #91a2bc;
        font-size: 13px;
        font-weight: 500;
    }

    &__actions {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 40px;
        gap: 8px;
        margin-top: 18px;
    }

    &__congrats,
    &__next {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 36px;
        border-radius: 8px;
        font-weight: 800;
        cursor: default;
    }

    &__congrats {
        gap: 7px;
        border: 0;
        background: #625bf6;
        color: #fff;
        font-size: 13px;

        .material-symbols-outlined {
            font-size: 16px;
        }
    }

    &__next {
        border: 1px solid #dce5f5;
        background: #fff;
        color: #8da0bd;
        cursor: pointer;

        .material-symbols-outlined {
            font-size: 20px;
        }
    }

    &__dots {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        margin-top: 16px;
    }

    &__dot {
        width: 6px;
        height: 6px;
        border-radius: 999px;
        background: #dbe3f0;

        &--active {
            width: 18px;
            background: #625bf6;
        }
    }
}

.dark-theme {
    .mural-birthday {
        border-color: var(--borderColor);
        background: #0c1427;
        box-shadow: none;

        &__avatar {
            background: #15203c;
        }

        &__badge {
            border-color: #0c1427;
        }

        &__heading,
        h3 {
            color: var(--whiteColor);
        }

        p {
            color: var(--bodyColor);
        }

        &__next {
            border-color: var(--borderColor);
            background: #15203c;
            color: var(--bodyColor);
        }

        &__dot {
            background: #2a3652;

            &--active {
                background: #625bf6;
            }
        }
    }
}

@keyframes birthday-fade {
    from {
        opacity: 0;
        transform: translateX(8px);
    }

    to {
        opacity: 1;
        transform: translateX(0);
    }
}
</style>
